import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";

const SLIDES = [
  {
    src: "/screenshots/dashboard.jpg",
    title: "Nadzorna plošča",
    caption: "Trenutno vreme, luna, plan odstrela in zadnja obvestila – vse na enem mestu.",
  },
  {
    src: "/screenshots/karta.jpg",
    title: "Karta lovišča",
    caption: "Satelitska in topografska podlaga, meje LD, upravljavska območja, raznos vonja glede na veter.",
  },
  {
    src: "/screenshots/plan.jpg",
    title: "Plan odstrela",
    caption: "Napredek po kategorijah (jelenjad, srnjad, prašič…), avtomatsko štetje odstreljenih živali.",
  },
  {
    src: "/screenshots/dogodki.jpg",
    title: "Dogodki in prijave",
    caption: "Delovne akcije, občni zbori, skupinski lovi – z prijavo enega klika (Pridem / Ne pridem).",
  },
  {
    src: "/screenshots/obvestila.jpg",
    title: "Obvestila članom",
    caption: "Pomembna obvestila s push notifikacijo in e-mailom – doseg vseh članov v sekundah.",
  },
];

const AUTOPLAY_MS = 4500;

export const ScreenshotSlideshow = () => {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!playing) return;
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % SLIDES.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(timerRef.current);
  }, [playing]);

  const go = (next) => {
    setIdx((i) => (i + next + SLIDES.length) % SLIDES.length);
    // Reset timer
    if (timerRef.current) clearInterval(timerRef.current);
    if (playing) {
      timerRef.current = setInterval(() => {
        setIdx((i) => (i + 1) % SLIDES.length);
      }, AUTOPLAY_MS);
    }
  };

  const slide = SLIDES[idx];

  return (
    <section className="max-w-6xl mx-auto px-4 py-12 md:py-16" data-testid="section-screenshot-slideshow">
      <div className="text-center mb-8">
        <h2 className="text-3xl md:text-4xl font-bold text-emerald-900" style={{ fontFamily: "'Playfair Display', serif" }}>
          Poglejte, kako izgleda
        </h2>
        <p className="text-slate-700 mt-2">Prikaz aplikacije v resnični uporabi.</p>
      </div>

      <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-emerald-900/10 bg-emerald-900 aspect-[16/10]">
        {/* Slides */}
        {SLIDES.map((s, i) => (
          <img
            key={s.src}
            src={s.src}
            alt={s.title}
            loading="lazy"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}
            data-testid={`slide-${i}`}
          />
        ))}

        {/* Bottom gradient + caption */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent p-4 sm:p-6 pointer-events-none">
          <h3 className="text-white text-lg sm:text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
            {slide.title}
          </h3>
          <p className="text-white/90 text-sm sm:text-base mt-1 max-w-2xl">{slide.caption}</p>
        </div>

        {/* Arrows */}
        <button
          onClick={() => go(-1)}
          aria-label="Prejšnji"
          className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-emerald-900 shadow flex items-center justify-center transition"
          data-testid="slideshow-prev"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          onClick={() => go(1)}
          aria-label="Naslednji"
          className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-emerald-900 shadow flex items-center justify-center transition"
          data-testid="slideshow-next"
        >
          <ChevronRight className="h-6 w-6" />
        </button>

        {/* Play/pause */}
        <button
          onClick={() => setPlaying((p) => !p)}
          aria-label={playing ? "Ustavi" : "Predvajaj"}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/85 hover:bg-white text-emerald-900 shadow flex items-center justify-center transition"
          data-testid="slideshow-toggle"
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </button>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-4">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Slajd ${i + 1}`}
            className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-emerald-900" : "w-2 bg-emerald-900/30 hover:bg-emerald-900/50"}`}
            data-testid={`slideshow-dot-${i}`}
          />
        ))}
      </div>

      {/* Progress bar */}
      {playing && (
        <div className="mt-2 h-0.5 bg-emerald-900/10 rounded overflow-hidden max-w-6xl mx-auto">
          <div
            key={idx}
            className="h-full bg-emerald-900 animate-slideshow-progress"
            style={{ animation: `slideshow-progress ${AUTOPLAY_MS}ms linear` }}
          />
        </div>
      )}

      <style>{`
        @keyframes slideshow-progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </section>
  );
};

export default ScreenshotSlideshow;
